import clsx from 'clsx'
import { TableRow, type TableRowProps } from 'primitives/table'
import type {
  DataTableColumnVirtualizer,
  DataTableHeader,
  DataTableHeaderGroup,
  DataTableInstance,
  DataTableRowData,
  DataTableVirtualItem,
} from '../../types'
import { DataTableHeadCell } from './table-head-cell'

interface Props<TData extends DataTableRowData> extends TableRowProps {
  columnVirtualizer?: DataTableColumnVirtualizer
  headerGroup: DataTableHeaderGroup<TData>
  table: DataTableInstance<TData>
}

export const DataTableHeadRow = <TData extends DataTableRowData>({
  columnVirtualizer,
  headerGroup,
  table,
  ...rest
}: Props<TData>) => {
  const {
    options: { enableStickyHeader, layoutMode },
  } = table

  const { virtualColumns, virtualPaddingLeft, virtualPaddingRight } = columnVirtualizer ?? {}

  const tableRowProps = {
    headerGroup,
    table,
    ...rest,
  }

  return (
    <TableRow
      {...tableRowProps}
      className={clsx(
        'table-row bg-background',
        enableStickyHeader && 'sticky top-0',
        layoutMode?.startsWith('grid') && 'flex',
        tableRowProps?.className,
      )}
    >
      {virtualPaddingLeft ? (
        <th className="flex" style={{ width: `${virtualPaddingLeft}px` }} aria-hidden />
      ) : null}
      {(virtualColumns ?? headerGroup.headers).map((headerOrVirtualHeader, renderedHeaderIndex) => {
        let header = headerOrVirtualHeader as DataTableHeader<TData>
        if (columnVirtualizer) {
          renderedHeaderIndex = (headerOrVirtualHeader as DataTableVirtualItem).index
          header = headerGroup.headers[renderedHeaderIndex]
        }

        return (
          <DataTableHeadCell
            columnVirtualizer={columnVirtualizer}
            header={header}
            key={header.id}
            renderedHeaderIndex={renderedHeaderIndex}
            table={table}
          />
        )
      })}
      {virtualPaddingRight ? (
        <th className="flex" style={{ width: `${virtualPaddingRight}px` }} aria-hidden />
      ) : null}
    </TableRow>
  )
}
